import { providers } from '../../providers';
import { alibaba } from '../alibaba';
import { baidu } from '../baidu';
import { kie } from '../kie';
import { getUiOptionsForModel } from '../ui-options';
import type { VideoModelEntry } from '../types';

// 价格表: 人民币/秒
// Happy Horse 1.0
const HAPPYHORSE10 = { '720p': 0.9, '1080p': 1.6 };
// Wan 3.0
const WAN30 = { '480p': 0.3, '720p': 0.6, '1080p': 1.2 };
// Wan 3.0 Prime
const WAN30_PRIME = { '480p': 0.45, '720p': 0.9, '1080p': 1.8 };
// Wan 2.7
const WAN27 = { '720p': 0.6, '1080p': 1 };
// Wan 2.6 Flash
const WAN26F = { '720p': 0.15, '1080p': 0.3 };
const WAN26F_AUDIO = { '720p': 0.3, '1080p': 0.5 };
// 查找价格函数
const lookup = (table: Record<string, number>, quality?: string) =>
    table[quality?.toLowerCase() ?? ''] ?? Math.max(...Object.values(table));
// 美元汇率
const RMB_TO_USD = 1 / 6.8;
// rmb to usd 转换
const usd = (rmb: number) => rmb * RMB_TO_USD;
// 系数
const COST_FACTOR = 1.5;

export const alibabaModels: VideoModelEntry[] = [
    {
        id: 'wan-3.0',
        chef: providers.alibaba,
        frameModeLabels: { ref: 'Omni Reference' },
        providers: [
            {
                ...providers.alibaba,
                model: kie('wan-3.0'),
                getCost: ({ quality, duration }) => {
                    return usd(duration * lookup(WAN30, quality)) * COST_FACTOR;
                },
                getPassCount: ({ quality, duration }) => {
                    return usd(duration * lookup(WAN30, quality)) * COST_FACTOR * 100;
                },
            },
        ],
        configByMode: {
            none: {
                aspectRatios: ['16:9', '4:3', '1:1', '3:4', '9:16'],
                durations: [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30],
                qualities: ['480P', '720P', '1080P'],
                inlineMedia: {
                    image: { allow: false },
                    video: { allow: false },
                    audio: { allow: false },
                },
            },
            first: {
                aspectRatios: ['match', '16:9', '4:3', '1:1', '3:4', '9:16'],
                durations: [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30],
                qualities: ['480P', '720P', '1080P'],
                inlineMedia: {
                    image: { allow: true, maxCount: 1, maxSize: '20MB', formats: ['image/png', 'image/jpeg', 'image/webp'] },
                    video: { allow: false },
                    audio: { allow: false },
                },
            },
            first_last: {
                aspectRatios: ['match', '16:9', '4:3', '1:1', '3:4', '9:16'],
                durations: [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30],
                qualities: ['480P', '720P', '1080P'],
                inlineMedia: {
                    image: { allow: true, maxCount: 2, maxSize: '20MB', formats: ['image/png', 'image/jpeg', 'image/webp'] },
                    video: { allow: false },
                    audio: { allow: false },
                },
            },
            ref: {
                aspectRatios: ['match', '16:9', '4:3', '1:1', '3:4', '9:16'],
                durations: [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30],
                qualities: ['480P', '720P', '1080P'],
                inlineMedia: {
                    allowInline: true,
                    image: { allow: true, maxCount: 10, maxSize: '20MB', formats: ['image/png', 'image/jpeg', 'image/webp'] },
                    video: { allow: true, maxCount: 5, maxSize: '100MB', minDuration: '1s', maxDuration: '15s', formats: ['video/mp4', 'video/mov'] },
                    audio: { allow: true, maxCount: 5, maxSize: '15MB', minDuration: '1s', maxDuration: '15s', formats: ['audio/mpeg', 'audio/wav'] },
                    mediaDurationLimit: { enabled: true, maxTotalDuration: '15s' },
                    maxMediaCount: 10,
                    maxTotalCount: 20,
                },
            },
        },
        precheckByMode: {
            none: ['prompt'],
            first: ['prompt', 'image'],
            first_last: ['prompt', 'image2'],
            ref: ['prompt', 'image|video|audio'],
        },
        supportsEdit: true,
        supportsInput: ['t2v', 'first', 'first_last', 'reference'],
        priceIndicator: 'lowest',
        price: 'Dynamic',
        uiOptions: getUiOptionsForModel('wan-3.0'),
    },
    {
        id: 'wan-3.0-prime',
        chef: providers.alibaba,
        frameModeLabels: { ref: 'Omni Reference' },
        providers: [
            {
                ...providers.alibaba,
                model: kie('wan-3.0-prime'),
                getCost: ({ quality, duration }) => {
                    return usd(duration * lookup(WAN30_PRIME, quality)) * COST_FACTOR;
                },
                getPassCount: ({ quality, duration }) => {
                    return usd(duration * lookup(WAN30_PRIME, quality)) * COST_FACTOR * 100;
                },
            },
        ],
        configByMode: {
            none: {
                aspectRatios: ['16:9', '4:3', '1:1', '3:4', '9:16'],
                durations: [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30],
                qualities: ['480P', '720P', '1080P'],
                inlineMedia: {
                    image: { allow: false },
                    video: { allow: false },
                    audio: { allow: false },
                },
            },
            first: {
                aspectRatios: ['match', '16:9', '4:3', '1:1', '3:4', '9:16'],
                durations: [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30],
                qualities: ['480P', '720P', '1080P'],
                inlineMedia: {
                    image: { allow: true, maxCount: 1, maxSize: '20MB', formats: ['image/png', 'image/jpeg', 'image/webp'] },
                    video: { allow: false },
                    audio: { allow: false },
                },
            },
            first_last: {
                aspectRatios: ['match', '16:9', '4:3', '1:1', '3:4', '9:16'],
                durations: [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30],
                qualities: ['480P', '720P', '1080P'],
                inlineMedia: {
                    image: { allow: true, maxCount: 2, maxSize: '20MB', formats: ['image/png', 'image/jpeg', 'image/webp'] },
                    video: { allow: false },
                    audio: { allow: false },
                },
            },
            ref: {
                aspectRatios: ['match', '16:9', '4:3', '1:1', '3:4', '9:16'],
                durations: [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30],
                qualities: ['480P', '720P', '1080P'],
                inlineMedia: {
                    allowInline: true,
                    image: { allow: true, maxCount: 10, maxSize: '20MB', formats: ['image/png', 'image/jpeg', 'image/webp'] },
                    video: { allow: true, maxCount: 5, maxSize: '100MB', minDuration: '1s', maxDuration: '15s', formats: ['video/mp4', 'video/mov'] },
                    audio: { allow: true, maxCount: 5, maxSize: '15MB', minDuration: '1s', maxDuration: '15s', formats: ['audio/mpeg', 'audio/wav'] },
                    mediaDurationLimit: { enabled: true, maxTotalDuration: '15s' },
                    maxMediaCount: 10,
                    maxTotalCount: 20,
                },
            },
        },
        precheckByMode: {
            none: ['prompt'],
            first: ['prompt', 'image'],
            first_last: ['prompt', 'image2'],
            ref: ['prompt', 'image|video|audio'],
        },
        supportsEdit: true,
        supportsInput: ['t2v', 'first', 'first_last', 'reference'],
        price: 'Dynamic',
        uiOptions: getUiOptionsForModel('wan-3.0-prime'),
    },
    {
        id: 'happyhorse-1.1',
        chef: providers.alibaba,
        providers: [
            {
                ...providers.alibaba,
                model: alibaba('happyhorse-1.1'),
                modelBackup: [baidu('happyhorse-1.1')],
                getCost: ({ quality, duration }) => {
                    return usd(duration * lookup(HAPPYHORSE10, quality)) * COST_FACTOR;
                },
                getPassCount: ({ quality, duration }) => {
                    return usd(duration * lookup(HAPPYHORSE10, quality)) * COST_FACTOR * 100;
                },
            },
        ],
        configByMode: {
            none: {
                aspectRatios: ['21:9', '9:21', '16:9', '9:16', '4:5', '5:4', '4:3', '3:4', '1:1'],
                durations: [3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15],
                qualities: ['720P', '1080P'],
                inlineMedia: {
                    image: { allow: false },
                    video: { allow: false },
                    audio: { allow: false },
                },
            },
            first: {
                aspectRatios: [],
                durations: [3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15],
                qualities: ['720P', '1080P'],
                inlineMedia: {
                    image: { allow: true, maxCount: 1, maxSize: '10MB', formats: ['image/png', 'image/jpeg'] },
                    video: { allow: false },
                    audio: { allow: false },
                },
            },
            ref: {
                aspectRatios: ['21:9', '9:21', '16:9', '9:16', '4:5', '5:4', '4:3', '3:4', '1:1'],
                durations: [3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15],
                qualities: ['720P', '1080P'],
                inlineMedia: {
                    allowInline: true,
                    image: { allow: true, maxCount: 9, maxSize: '10MB', formats: ['image/png', 'image/jpeg'] },
                    video: { allow: false },
                    audio: { allow: false },
                },
            },
        },
        precheckByMode: {
            none: ['prompt'],
            first: ['prompt', 'image'],
            ref: ['prompt', 'image'],
        },
        supportsEdit: false,
        supportsInput: ['t2v', 'first', 'reference'],
        price: 'Dynamic',
    },
    {
        id: 'happyhorse-1.1-v2v',
        chef: providers.alibaba,
        providers: [
            {
                ...providers.alibaba,
                model: alibaba('happyhorse-1.1-v2v'),
                modelBackup: [baidu('happyhorse-1.1-v2v')],
                getCost: ({ quality, providerOptions }) => {
                    const inputDur = providerOptions?._media?.videoDurationSeconds ?? 15;
                    return usd(inputDur * lookup(HAPPYHORSE10, quality)) * COST_FACTOR;
                },
                getPassCount: ({ quality, providerOptions }) => {
                    const inputDur = providerOptions?._media?.videoDurationSeconds ?? 15;
                    return usd(inputDur * lookup(HAPPYHORSE10, quality)) * COST_FACTOR * 100;
                },
            },
        ],
        configByMode: {
            v2v: {
                aspectRatios: [],
                durations: [],
                qualities: ['720P', '1080P'],
                inlineMedia: {
                    image: { allow: true, maxCount: 5, maxSize: '10MB', formats: ['image/png', 'image/jpeg'] },
                    video: { allow: true, maxCount: 1, maxSize: '100MB', minDuration: '1s', maxDuration: '15s', formats: ['video/mp4', 'video/mov'] },
                    audio: { allow: false },
                },
            },
        },
        precheckByMode: {
            v2v: ['prompt', 'video'],
        },
        supportsEdit: true,
        supportsInput: ['v2v'],
        priceIndicator: 'lowest',
        price: 'Dynamic',
    },
    {
        id: 'happyhorse-1.0',
        chef: providers.alibaba,
        providers: [
            {
                ...providers.alibaba,
                model: alibaba('happyhorse-1.0'),
                modelBackup: [baidu('happyhorse-1.0')],
                getCost: ({ quality, duration }) => {
                    return usd(duration * lookup(HAPPYHORSE10, quality)) * COST_FACTOR;
                },
                getPassCount: ({ quality, duration }) => {
                    return usd(duration * lookup(HAPPYHORSE10, quality)) * COST_FACTOR * 100;
                },
            },
        ],
        configByMode: {
            none: {
                aspectRatios: ['21:9', '9:21', '16:9', '9:16', '4:5', '5:4', '4:3', '3:4', '1:1'],
                durations: [3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15],
                qualities: ['720P', '1080P'],
                inlineMedia: {
                    image: { allow: false },
                    video: { allow: false },
                    audio: { allow: false },
                },
            },
            first: {
                aspectRatios: [],
                durations: [3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15],
                qualities: ['720P', '1080P'],
                inlineMedia: {
                    image: { allow: true, maxCount: 1, maxSize: '10MB', formats: ['image/png', 'image/jpeg'] },
                    video: { allow: false },
                    audio: { allow: false },
                },
            },
            ref: {
                aspectRatios: ['21:9', '9:21', '16:9', '9:16', '4:5', '5:4', '4:3', '3:4', '1:1'],
                durations: [3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15],
                qualities: ['720P', '1080P'],
                inlineMedia: {
                    allowInline: true,
                    image: { allow: true, maxCount: 9, maxSize: '10MB', formats: ['image/png', 'image/jpeg'] },
                    video: { allow: false },
                    audio: { allow: false },
                },
            },
        },
        precheckByMode: {
            none: ['prompt'],
            first: ['prompt', 'image'],
            ref: ['prompt', 'image'],
        },
        supportsEdit: false,
        supportsInput: ['t2v', 'first', 'reference'],
        priceIndicator: 'lowest',
        price: 'Dynamic',
    },
    {
        id: 'happyhorse-1.0-v2v',
        chef: providers.alibaba,
        providers: [
            {
                ...providers.alibaba,
                model: alibaba('happyhorse-1.0-v2v'),
                modelBackup: [baidu('happyhorse-1.0-v2v')],
                getCost: ({ quality, providerOptions }) => {
                    const inputDur = providerOptions?._media?.videoDurationSeconds ?? 15;
                    return usd(inputDur * lookup(HAPPYHORSE10, quality)) * COST_FACTOR;
                },
                getPassCount: ({ quality, providerOptions }) => {
                    const inputDur = providerOptions?._media?.videoDurationSeconds ?? 15;
                    return usd(inputDur * lookup(HAPPYHORSE10, quality)) * COST_FACTOR * 100;
                },
            },
        ],
        configByMode: {
            v2v: {
                aspectRatios: [],
                durations: [],
                qualities: ['720P', '1080P'],
                inlineMedia: {
                    image: { allow: true, maxCount: 5, maxSize: '10MB', formats: ['image/png', 'image/jpeg'] },
                    video: { allow: true, maxCount: 1, maxSize: '100MB', minDuration: '1s', maxDuration: '15s', formats: ['video/mp4', 'video/mov'] },
                    audio: { allow: false },
                },
            },
        },
        precheckByMode: {
            v2v: ['prompt', 'video'],
        },
        supportsEdit: true,
        supportsInput: ['v2v'],
        priceIndicator: 'lowest',
        price: 'Dynamic',
    },
    {
        id: 'wan-2.7',
        chef: providers.alibaba,
        providers: [
            {
                ...providers.alibaba,
                model: alibaba('wan-2.7'),
                getCost: ({ quality, duration }) => {
                    return usd(duration * lookup(WAN27, quality)) * COST_FACTOR;
                },
                getPassCount: ({ quality, duration }) => {
                    return usd(duration * lookup(WAN27, quality)) * COST_FACTOR * 100;
                },
            },
        ],
        configByMode: {
            none: {
                aspectRatios: ['16:9', '9:16', '4:3', '3:4', '1:1'],
                durations: [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15],
                qualities: ['720P', '1080P'],
                inlineMedia: {
                    image: { allow: false },
                    video: { allow: false },
                    audio: { allow: false },
                },
            },
            first: {
                aspectRatios: [],
                durations: [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15],
                qualities: ['720P', '1080P'],
                inlineMedia: {
                    image: { allow: true, maxCount: 1, maxSize: '10MB', formats: ['image/png', 'image/jpeg'] },
                    video: { allow: false },
                    audio: { allow: false },
                },
            },
            ref: {
                aspectRatios: ['16:9', '9:16', '4:3', '3:4', '1:1'],
                durations: [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15],
                qualities: ['720P', '1080P'],
                inlineMedia: {
                    allowInline: true,
                    image: { allow: true, maxCount: 5, maxSize: '10MB', formats: ['image/png', 'image/jpeg'] },
                    video: { allow: false },
                    audio: { allow: false },
                },
            },
        },
        precheckByMode: {
            none: ['prompt'],
            first: ['prompt', 'image'],
            ref: ['prompt', 'image'],
        },
        supportsEdit: false,
        supportsInput: ['t2v', 'first', 'reference'],
        priceIndicator: 'lowest',
        price: 'Dynamic',
    },
    {
        id: 'wan-2.7-v2v',
        chef: providers.alibaba,
        providers: [
            {
                ...providers.alibaba,
                model: alibaba('wan-2.7-v2v'),
                getCost: ({ quality, providerOptions }) => {
                    const inputDur = providerOptions?._media?.videoDurationSeconds ?? 15;
                    return usd(inputDur * lookup(WAN27, quality)) * COST_FACTOR;
                },
                getPassCount: ({ quality, providerOptions }) => {
                    const inputDur = providerOptions?._media?.videoDurationSeconds ?? 15;
                    return usd(inputDur * lookup(WAN27, quality)) * COST_FACTOR * 100;
                },
            },
        ],
        configByMode: {
            v2v: {
                aspectRatios: [],
                durations: [],
                qualities: ['720P', '1080P'],
                inlineMedia: {
                    image: { allow: true, maxCount: 4, maxSize: '20MB', formats: ['image/png', 'image/jpeg'] },
                    video: { allow: true, maxCount: 1, maxSize: '100MB', minDuration: '1s', maxDuration: '15s', formats: ['video/mp4', 'video/mov'] },
                    audio: { allow: false },
                },
            },
        },
        precheckByMode: {
            v2v: ['prompt', 'video'],
        },
        supportsEdit: true,
        supportsInput: ['v2v'],
        priceIndicator: 'lowest',
        price: 'Dynamic',
    },
    {
        id: 'wan-2.6-flash',
        chef: providers.alibaba,
        providers: [
            {
                ...providers.alibaba,
                model: alibaba('wan-2.6-flash'),
                getCost: ({ quality, duration, providerOptions }) => {
                    const withAudio = providerOptions?.audio !== false;
                    return usd(duration * lookup(withAudio ? WAN26F_AUDIO : WAN26F, quality)) * COST_FACTOR;
                },
                getPassCount: ({ quality, duration, providerOptions }) => {
                    const withAudio = providerOptions?.audio !== false;
                    return usd(duration * lookup(withAudio ? WAN26F_AUDIO : WAN26F, quality)) * COST_FACTOR * 100;
                },
            },
        ],
        configByMode: {
            first: {
                aspectRatios: [],
                durations: [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15],
                qualities: ['720P', '1080P'],
                inlineMedia: {
                    image: { allow: true, maxCount: 1, maxSize: '10MB', formats: ['image/png', 'image/jpeg'] },
                    video: { allow: false },
                    audio: { allow: false },
                },
            },
            ref: {
                aspectRatios: ['16:9', '9:16', '4:3', '3:4', '1:1'],
                durations: [2, 3, 4, 5, 6, 7, 8, 9, 10],
                qualities: ['720P', '1080P'],
                inlineMedia: {
                    allowInline: true,
                    image: { allow: true, maxCount: 5, maxSize: '20MB', formats: ['image/png', 'image/jpeg'] },
                    video: { allow: false },
                    audio: { allow: false },
                },
            },
        },
        precheckByMode: {
            first: ['prompt', 'image'],
            ref: ['prompt', 'image'],
        },
        supportsEdit: false,
        supportsInput: ['first', 'reference'],
        priceIndicator: 'lowest',
        price: 'Dynamic',
    },
];